import dayjs from 'dayjs';

export interface CollectorValue {
  collectorType: 1 | 2;
  collectorId?: number;
  collectorName?: string;
}
export interface TaskFormValues {
  projectId: { value: number; label: string };
  spotId: { value: number; label: string };
  remark?: string;
  flowEnabled: boolean;
  brandEnabled: boolean;
  competitorEnabled?: boolean;
  competitors?: { value: number; label: string }[];
  competitorMode?: 'flow' | 'custom';
  competitorDate?: string;
  competitorCollector?: CollectorValue;
  flowRecords?: { date: string; collector?: CollectorValue }[];
  periods?: { start: string; end: string }[];
  brandMode?: 'flow' | 'custom';
  brandDate?: string;
  brandCollector?: CollectorValue;
}

/** 散选日期按升序生成记录，增删日期时保留同一天的采集人。 */
export function syncFlowDates(dates: string[], records: NonNullable<TaskFormValues['flowRecords']> = []) {
  return Array.from(new Set(dates)).sort().map(date => ({
    date,
    collector: records.find(record => record.date === date)?.collector,
  }));
}

/** 创建任务与 H5 一致，禁止新增历史日期。 */
export function isPastCollectDate(date: any) {
  return dayjs(date).isBefore(dayjs(), 'day');
}

/** 与移动端一致：服务商不携带员工身份。 */
export function collectorParams(value?: CollectorValue) {
  if (value?.collectorType === 2) {
    return { collectorType: 2, collectorId: null, collectorName: null };
  }
  if (!value?.collectorId) throw new Error('请选择采集人');
  return { collectorType: 1, collectorId: value.collectorId, collectorName: value.collectorName };
}

/** 构建创建契约；关闭模块不提交其草稿，周边跟随首个客流日期及采集人。 */
export function buildCheckSpotTask(values: TaskFormValues) {
  const records = values.flowRecords || [];
  const periods = values.periods || [];
  const hasFlow = values.flowEnabled && (records.length > 0 || periods.some(item => item.start || item.end));
  const follow = values.brandMode === 'flow' && values.flowEnabled && records.length > 0;
  const brandDate = follow ? records[0]?.date : values.brandDate;
  const brandCollector = values.brandCollector || (follow ? records[0]?.collector : undefined);
  const hasBrand = values.brandEnabled && !!(brandDate || brandCollector);
  const competitorFollow = values.competitorMode === 'flow' && values.flowEnabled && records.length > 0;
  const competitorDate = competitorFollow ? records[0]?.date : values.competitorDate;
  const competitorCollector = values.competitorCollector || (competitorFollow ? records[0]?.collector : undefined);
  const competitors = values.competitors || [];
  const hasCompetitor = values.competitorEnabled && !!(competitors.length || competitorDate || competitorCollector);
  if (!hasFlow && !hasBrand && !hasCompetitor) throw new Error('客流信息、周边信息、竞品调研至少填写一项');
  if (hasFlow && (!records.length || !periods.length || records.some(item => !item.date || !item.collector))) throw new Error('请完善客流信息');
  const flowRecords = hasFlow ? records.map(item => ({
    collectDate: item.date, periodJson: periods, ...collectorParams(item.collector),
  })) : [];
  if (hasBrand && (!brandDate || !brandCollector)) throw new Error('请完善周边信息');
  if (hasCompetitor && (!competitors.length || !competitorDate || !competitorCollector)) throw new Error('请完善竞品调研');
  return {
    nameBySpot: true,
    projectId: values.projectId.value,
    projectName: values.projectId.label,
    spotId: values.spotId.value,
    spotName: values.spotId.label,
    remark: values.remark || '',
    flowRecords,
    ...(hasCompetitor ? { competitorResearch: {
      competitors: competitors.map(item => ({ competitorId: item.value, competitorName: item.label })),
      collectedAt: competitorDate,
      ...collectorParams(competitorCollector),
    } } : {}),
    ...(hasBrand ? { aroundBrand: {
      collectedAt: brandDate,
      ...collectorParams(brandCollector),
    } } : {}),
  };
}
